<template lang="pug">
.cleaning__wrapper
  .cleaning__wrapper-filters
    filters-component(
      ref="filtersRef",
      :dateFrom="dateFrom",
      :dateTo="dateTo",
      :dateType="dateType",
      :customDateFrom="customDateFrom",
      :customDateTo="customDateTo",
      :initialFilters="filtersValues",
      :facilityId="facilityId",
      @setTitle="setTitle",
      @updateDateType="updateDateType",
      @submit="updateFilters",
      @updateCustomDateFrom="updateCustomDateFrom",
      @updateCustomDateTo="updateCustomDateTo"
    )
    .cleaning__wrapper-split-line
  header-component.cleaning__wrapper-header(
    ref="headerRef",
    :facilityId="facilityId",
    :dateType="dateType",
    :dateFrom="dateFrom",
    :dateTo="dateTo",
    :customDateFrom="customDateFrom",
    :customDateTo="customDateTo",
    :additionalFilters="filtersValues"
  )
  .cleaning__wrapper-chart
    activity-chart(
      ref="chartRef",
      :facilityId="facilityId",
      :dateFrom="dateFrom",
      :dateTo="dateTo",
      :customDateFrom="customDateFrom",
      :customDateTo="customDateTo",
      :dateType="dateType",
      :additionalFilters="filtersValues"
    )
  .cleaning__wrapper-split-line
  table-component.cleaning__wrapper-table__component(
    ref="buildingsTableRef",
    tableName="buildings",
    :facilityId="facilityId",
    :dateFrom="dateFrom",
    :dateTo="dateTo",
    :customDateFrom="customDateFrom",
    :customDateTo="customDateTo",
    :dateType="dateType",
    :additionalFilters="filtersValues"
  )
  table-component.cleaning__wrapper-table__component(
    ref="companiesTableRef",
    tableName="companies",
    :facilityId="facilityId",
    :dateFrom="dateFrom",
    :dateTo="dateTo",
    :dateType="dateType",
    :customDateFrom="customDateFrom",
    :customDateTo="customDateTo",
    :additionalFilters="filtersValues"
  )
  table-component.cleaning__wrapper-table__component(
    ref="employeesTableRef",
    tableName="employees",
    :facilityId="facilityId",
    :dateFrom="dateFrom",
    :dateTo="dateTo",
    :dateType="dateType",
    :customDateFrom="customDateFrom",
    :customDateTo="customDateTo",
    :additionalFilters="filtersValues"
  )
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import headerComponent from "./cleaning/header.vue";
import tableComponent from "./cleaning/table.vue";
import activityChart from "./cleaning/activityChart.vue";
import filtersComponent from "./cleaning/filters.vue";
import { useRouter } from "@/routing/router";

const router = useRouter();

const emit = defineEmits([
  /**
   * Срабатывает при загрузке страницы. Пример параметров: false
   */
  "set-loading",
  /**
   * Срабатывает при получении даты страницы. Нужен для выставление заголовка страницы. Пример параметров: "Лукойл"
   */
  "set-title",
]);

const props = defineProps({
  /* id текущей основной орагнизации выбранной пользователем для дашборда в фильтрах */
  facilityId: { type: Number, default: -1 },
});

const dateType = ref("today");
const filtersValues = ref({});
const headerRef = ref(null);
const filtersRef = ref(null);
const chartRef = ref(null);
const buildingsTableRef = ref(null);
const companiesTableRef = ref(null);
const employeesTableRef = ref(null);
const customDateFrom = ref("");
const customDateTo = ref("");

const facilityId = computed(() => props.facilityId);

const dateFrom = computed(() => {
  const currentDateTime = new Date();
  const currentDay = currentDateTime.getDate();
  const currentMonth = currentDateTime.getMonth();
  const currentYear = currentDateTime.getFullYear();

  const yesterdayDateTime = new Date();
  yesterdayDateTime.setDate(currentDay - 1);
  const yesterdayDay = yesterdayDateTime.getDate();
  const yesterdayMonth = yesterdayDateTime.getMonth();
  const yesterdayYear = yesterdayDateTime.getFullYear();

  if (dateType.value === "today") {
    const currentDate = new Date(`${currentYear}-${currentMonth + 1}-${currentDay}`);
    currentDate.setHours(0, 0, 0, 0);
    return currentDate.toISOString();
  } else if (dateType.value === "yesterday") {
    const yesterdayDate = new Date(`${yesterdayYear}-${yesterdayMonth + 1}-${yesterdayDay}`);
    yesterdayDate.setHours(0, 0, 0, 0);
    return yesterdayDate.toISOString();
  } else if (dateType.value === "current_month") {
    const firstDayOfMonth = new Date(`${currentYear}-${currentMonth + 1}-1`);
    return firstDayOfMonth.toISOString();
  } else if (dateType.value === "last_month") {
    const firstDayOfLastMonth = new Date(`${currentYear}-${currentMonth}-${1}`);
    return firstDayOfLastMonth.toISOString();
  } else {
    return "";
  }
});

const dateTo = computed(() => {
  const currentDateTime = new Date();
  const currentDay = currentDateTime.getDate();
  const currentMonth = currentDateTime.getMonth();
  const currentYear = currentDateTime.getFullYear();
  const yesterdayDateTime = new Date();
  yesterdayDateTime.setDate(currentDay - 1);
  if (dateType.value === "today") {
    return currentDateTime.toISOString();
  } else if (dateType.value === "yesterday") {
    const yesterdayDateLastHour = new Date(yesterdayDateTime);
    yesterdayDateLastHour.setHours(24, 0, 0, 0);
    return yesterdayDateLastHour.toISOString();
  } else if (dateType.value === "current_month") {
    return currentDateTime.toISOString();
  } else if (dateType.value === "last_month") {
    const lastDayOfLastMonth = new Date(`${currentYear}-${currentMonth + 1}-1`);
    lastDayOfLastMonth.setDate(0);
    lastDayOfLastMonth.setHours(23, 59, 59, 999);
    return lastDayOfLastMonth.toISOString();
  } else {
    return "";
  }
});

const updateCustomDateFrom = val => {
  customDateFrom.value = val;
  updateFiltersWithDate();
};

const updateCustomDateTo = val => {
  customDateTo.value = val;
  updateFiltersWithDate();
};

const updateFiltersWithDate = () => {
  if (customDateTo.value && customDateFrom.value) {
    refreshPage();
  }
};

const refreshPage = async () => {
  await nextTick();
  await filtersRef.value?.resetDateFields();
  await headerRef.value?.getData();
  await chartRef.value?.getData();
  await buildingsTableRef.value?.refreshPage();
  await companiesTableRef.value?.refreshPage();
  await employeesTableRef.value?.refreshPage();
};

const changeFacility = async filters => {
  if (facilityId.value !== filters.facilityId) {
    // Загружаем новую страницу если фасилити изменился
    router.push({ name: "Dashboard", params: { facility_id: filters.facilityId, type: "cleaning_dashboard" } });
  }
  await refreshPage();
};

const setTitle = title => {
  emit("set-title", title);
};

const updateDateType = async type => {
  dateType.value = type.value;
  if (dateType.value === "custom") {
    customDateFrom.value = "";
    customDateTo.value = "";
    await filtersRef.value?.resetDateFields();
    return;
  }
  await refreshPage();
};

const updateFilters = async filters => {
  filtersValues.value = filters;
  await refreshPage();
};

onMounted(() => {
  emit("set-loading", false);
});

defineExpose({ changeFacility });
</script>

<style lang="scss" scoped>
.cleaning__wrapper {
  padding-top: 0 !important;

  &-split-line {
    margin: 20px 0;
    width: 100%;
    height: 1px;
    background-color: #dee4ff;
  }

  &-filters {
    width: 100%;
    display: block;
    position: sticky;
    top: 151px;
    padding-top: 24px;
    background-color: var(--dashboard-background-color);
    z-index: 4;

    &-title {
      font-size: 20px;
      margin-bottom: 20px;
    }
  }

  &-chart {
    margin-top: 20px;

    &-title {
      font-size: 20px;
      margin-bottom: 20px;
    }
  }

  &-table__component {
    &:not(:first-child) {
      margin-top: 16px;
    }

    &::-webkit-scrollbar-track {
      margin-top: 20px;
      margin-bottom: 20px;
    }
  }
}
</style>
